import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

const API_BASE_URL = process.env.REACT_APP_PERMISSIONS_API || '';

const CACHE_KEY_CATALOG = 'pinnacle-app-catalog';
const CACHE_KEY_PERMISSIONS = 'pinnacle-permissions';
const SQL_AUTH_APP_IDS = [
  'feas-pv-qc',
  'talent-screener',
  'newton-rbd-builder',
];

function getCatalogAppIds(catalog) {
  if (!catalog) return SQL_AUTH_APP_IDS;

  return [
    ...(Array.isArray(catalog.applications) ? catalog.applications : []),
    ...(Array.isArray(catalog.workplace) ? catalog.workplace : []),
    ...(Array.isArray(catalog.toolbox) ? catalog.toolbox : []),
  ]
    .map((app) => String(app?.id || '').trim().toLowerCase())
    .filter(Boolean);
}

/** Load cached data from localStorage (returns null if missing or corrupt) */
function loadCache(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/**
 * Custom hook to manage user permissions and app catalog.
 * All API calls use Auth0 JWT Bearer tokens for authentication.
 * The server reads the user's email from the verified JWT.
 *
 * Caching: On first load, renders from localStorage cache instantly
 * while fetching fresh data from the server in the background.
 */
export const usePermissions = () => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [permissions, setPermissions] = useState(() => loadCache(CACHE_KEY_PERMISSIONS));
  const [appCatalog, setAppCatalog] = useState(() => loadCache(CACHE_KEY_CATALOG));
  const [loading, setLoading] = useState(!loadCache(CACHE_KEY_CATALOG));
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [sqlAuthAccessByAppId, setSqlAuthAccessByAppId] = useState({});

  /**
   * Get an authorization header with the JWT access token
   */
  const getAuthHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, [getAccessTokenSilently]);

  /**
   * Fetch app catalog from server
   */
  const fetchAppCatalog = useCallback(async (headers) => {
    const response = await fetch(`${API_BASE_URL}/api/apps`, { headers });

    if (response.status === 403 || response.status === 401) {
      setAccessDenied(true);
      localStorage.removeItem(CACHE_KEY_CATALOG);
      localStorage.removeItem(CACHE_KEY_PERMISSIONS);
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch app catalog: ${response.statusText}`);
    }

    return response.json();
  }, []);

  /**
   * Fetch user permissions from server
   */
  const fetchPermissions = useCallback(async (headers) => {
    const response = await fetch(`${API_BASE_URL}/api/permissions`, {
      method: 'POST',
      headers,
    });

    if (response.status === 403 || response.status === 401) {
      setAccessDenied(true);
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch permissions: ${response.statusText}`);
    }

    return response.json();
  }, []);

  const fetchSqlAuthAccess = useCallback(async (headers, catalog) => {
    const applicationKeys = [...new Set(getCatalogAppIds(catalog))];

    try {
      const response = await fetch(`${API_BASE_URL}/api/access/check-bulk`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ applicationKeys }),
      });

      if (response.status !== 200) {
        return Object.fromEntries(
          SQL_AUTH_APP_IDS.map((appId) => [appId, { managed: true, allowed: false }])
        );
      }

      const result = await response.json();
      return result?.access && typeof result.access === 'object'
        ? result.access
        : {};
    } catch {
      return Object.fromEntries(
        SQL_AUTH_APP_IDS.map((appId) => [appId, { managed: true, allowed: false }])
      );
    }
  }, []);

  /**
   * Load both app catalog and permissions.
   * If cached data exists, it's already rendered — this fetches fresh data in the background.
   */
  const loadData = useCallback(async () => {
    if (!isAuthenticated || !user?.email) {
      setSqlAuthAccessByAppId({});
      setLoading(false);
      return;
    }

    const hasCachedCatalog = !!loadCache(CACHE_KEY_CATALOG);

    try {
      // Only show loading spinner if we have no cached data to show
      if (!hasCachedCatalog) setLoading(true);
      setError(null);
      setAccessDenied(false);
      setSqlAuthAccessByAppId({});

      const headers = await getAuthHeaders();

      // Fetch app catalog first — if access is denied, skip permissions call
      const catalog = await fetchAppCatalog(headers);

      if (!catalog) return; // 403/401 — accessDenied already set, no need for second call

      setAppCatalog(catalog);
      localStorage.setItem(CACHE_KEY_CATALOG, JSON.stringify(catalog));

      const [perms, sqlAuthAccess] = await Promise.all([
        fetchPermissions(headers),
        fetchSqlAuthAccess(headers, catalog),
      ]);

      setSqlAuthAccessByAppId(sqlAuthAccess);

      if (perms) {
        setPermissions(perms);
        localStorage.setItem(CACHE_KEY_PERMISSIONS, JSON.stringify(perms));
      }
    } catch (err) {
      console.error('Error loading platform data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.email, getAuthHeaders, fetchAppCatalog, fetchPermissions, fetchSqlAuthAccess]);

  /**
   * Check if user has access to a specific app
   */
  const hasAppAccess = useCallback((appId) => {
    const normalizedAppId = String(appId || '').trim().toLowerCase();
    const sqlAccess = sqlAuthAccessByAppId[normalizedAppId];

    if (sqlAccess?.managed) {
      return sqlAccess.restricted === true
        ? sqlAccess.allowed === true
        : true;
    }

    if (SQL_AUTH_APP_IDS.includes(normalizedAppId)) {
      return sqlAccess?.allowed === true;
    }

    if (!permissions) return false;

    if (permissions.appAccess && permissions.appAccess[normalizedAppId] !== undefined) {
      return permissions.appAccess[normalizedAppId];
    }

    return permissions.hasGeneralAccess !== false;
  }, [permissions, sqlAuthAccessByAppId]);

  /**
   * Fire-and-forget signal that the user launched a given app, used for MAU
   * reporting. Must never throw or block navigation to the app.
   */
  const trackAppLaunch = useCallback(async (applicationKey) => {
    if (!isAuthenticated || !user?.email || !applicationKey) return;

    try {
      const headers = await getAuthHeaders();
      await fetch(`${API_BASE_URL}/api/access/track-launch`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ applicationKey }),
      });
    } catch {
      // Ignore — launch tracking must never break the launch flow.
    }
  }, [isAuthenticated, user?.email, getAuthHeaders]);

  /**
   * Refresh all data (clears cache first)
   */
  const refreshPermissions = useCallback(() => {
    localStorage.removeItem(CACHE_KEY_CATALOG);
    localStorage.removeItem(CACHE_KEY_PERMISSIONS);
    loadData();
  }, [loadData]);

  // Fetch on mount or when auth state changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    permissions,
    appCatalog,
    loading,
    error,
    accessDenied,
    hasAppAccess,
    trackAppLaunch,
    refreshPermissions,
    hasGeneralAccess: permissions?.hasGeneralAccess !== false,
  };
};

export default usePermissions;
